import React, { useEffect, useState, useRef } from 'react';
import { Row, Col, Dropdown, Spinner, Button } from 'react-bootstrap';
import { getAll, getSurveyById } from 'services/surveys';
import { useReactToPrint } from 'react-to-print';
import PropTypes from 'prop-types';

import ResponsesTable from 'components/app/reports/components/ResponsesTables';
import SurveyCounters from 'components/app/reports/components/SurveyCounters';
import SurveyResponses from 'components/app/reports/components/SurveyResponses';
import DashboardPrint from 'components/app/reports/components/DashboardPrint';

const Dashboard = ({ sharedSurveyId = null }) => {
  const [surveys, setSurveys] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const printRef = useRef();

  // Si tenemos un survey compartido, cargar solo ese survey
  const fetchSharedSurvey = () => {
    if (sharedSurveyId) {
      setLoading(true);
      getSurveyById(sharedSurveyId)
        .then(response => {
          if (response.result && response.survey) {
            const survey = response.survey;
            setSurveys([survey]);
            setSelectedSurvey(survey);
          } else {
            setSurveys([]);
            setSelectedSurvey(null);
          }
        })
        .catch(error => {
          console.error('Error al cargar encuesta compartida:', error);
          setSurveys([]);
          setSelectedSurvey(null);
        })
        .finally(() => setLoading(false));
    }
  };

  const fetchSurveys = () => {
    setLoading(true);
    getAll(1)
      .then(response => {
        if (Array.isArray(response.surveys)) {
          setSurveys(response.surveys);
        } else {
          setSurveys([]);
        }
      })
      .catch(() => setSurveys([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (sharedSurveyId) {
      fetchSharedSurvey();
    } else {
      fetchSurveys();
    }
  }, [sharedSurveyId]);

  const handleSelectSurvey = survey => {
    setSelectedSurvey(survey);
    console.log('Encuesta seleccionada:', survey);
  };

  const handleRefresh = () => {
    if (sharedSurveyId) {
      fetchSharedSurvey();
    } else {
      fetchSurveys();
    }
    setRefreshKey(prev => prev + 1);
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: selectedSurvey
      ? `Reporte - ${selectedSurvey.name}`
      : 'Reporte encuesta',
    removeAfterPrint: true
  });

  return (
    <>
      <Row className="g-3 mb-3">
        <Col md={10} xxl={5}>
          {loading ? (
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Cargando...</span>
            </Spinner>
          ) : (
            <Dropdown>
              <Dropdown.Toggle
                variant="primary"
                id="dropdown-basic"
                style={{ width: '100%', textAlign: 'left' }}
                aria-label="Seleccionar encuesta"
                className="text-truncate"
              >
                <span
                  className="text-truncate d-inline-block"
                  style={{ maxWidth: '90%' }}
                  title={
                    selectedSurvey
                      ? selectedSurvey.name
                      : 'Selecciona una encuesta'
                  }
                >
                  {selectedSurvey
                    ? selectedSurvey.name
                    : 'Selecciona una encuesta'}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu
                style={{ width: '100%', maxHeight: '300px', overflowY: 'auto' }}
              >
                <Dropdown.Item
                  key={0}
                  onClick={() => handleSelectSurvey(null)}
                  aria-label="Sin encuesta seleccionada"
                  className="text-wrap"
                >
                  Sin encuesta seleccionada
                </Dropdown.Item>
                {surveys.map(survey => (
                  <Dropdown.Item
                    key={survey.id}
                    onClick={() => handleSelectSurvey(survey)}
                    aria-label={`Seleccionar encuesta ${survey.name}`}
                    className="text-wrap"
                    title={survey.name}
                    style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                  >
                    {survey.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </Col>
        <Col md={2} xxl={1} className="d-flex align-items-center">
          <Button
            variant="outline-secondary"
            onClick={handleRefresh}
            disabled={loading}
            title="Recargar encuestas"
          >
            &#x21bb;
          </Button>
        </Col>
        <Col md={12} className="d-flex justify-content-end mt-2">
          {selectedSurvey && (
            <Button variant="info" onClick={handlePrint}>
              Imprimir / Exportar PDF
            </Button>
          )}
        </Col>
      </Row>

      <div>
        {selectedSurvey == 0 || selectedSurvey == null ? (
          <div className="border shadow-sm text-center py-3 text-muted">
            Selecciona una encuesta para ver los datos.
          </div>
        ) : (
          <>
            <SurveyCounters
              key={`counters-${
                selectedSurvey ? selectedSurvey.id : 0
              }-${refreshKey}`}
              surveyId={selectedSurvey ? selectedSurvey.id : 0}
            />

            <SurveyResponses
              key={`openresponses-${
                selectedSurvey ? selectedSurvey.id : 0
              }-${refreshKey}`}
              surveyId={selectedSurvey ? selectedSurvey.id : 0}
            />

            <ResponsesTable
              key={`responses-${
                selectedSurvey ? selectedSurvey.id : 0
              }-${refreshKey}`}
              surveyId={selectedSurvey ? selectedSurvey.id : 0}
            />

            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100vw',
                visibility: 'hidden',
                zIndex: -1,
                height: 0,
                overflow: 'hidden'
              }}
            >
              <div ref={printRef}>
                <DashboardPrint
                  surveyId={selectedSurvey.id}
                  surveyName={selectedSurvey.name}
                  refreshKey={refreshKey}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

Dashboard.propTypes = {
  sharedSurveyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default Dashboard;
